import type { TaskItem } from '@Types/dashboard.types';

// DS-3309: payment_request tasks carry their purpose on taskData.purpose.
//   'emd'         → wire-instructions-only earnest money deposit (EMD copy/priming).
//   'cashToClose' → closing-funds digital payment (existing payment_c2c copy/priming).
//   'other'       → Other payment; its type and description live on the task (no closing-funds copy).
// Modeled as a real union so the discriminator is type-checked and the literals
// live in exactly one place instead of being matched ad-hoc across files/tests.
export type PaymentRequestPurpose = 'emd' | 'cashToClose' | 'other';

// Effective types produced for a payment_request, split by purpose. Exported as
// constants so the priming/completion/TaskCard lookup-map keys can reference them
// and stay in sync.
export const PAYMENT_REQUEST_EMD = 'payment_request_emd';
export const PAYMENT_REQUEST_C2C = 'payment_request_c2c';
export const PAYMENT_REQUEST_OTHER = 'payment_request_other';

// The two task types that are the same Match identity verification: `match` is the
// active task, `complete_match_request` the one a participant re-enters after a failed
// verification. Callers almost always mean both, and the pair was being re-spelled at
// each call site, so it lives here with the rest of the type-shape knowledge.
export const MATCH_TASK_TYPES: ReadonlySet<string> = new Set(['match', 'complete_match_request']);

export function isMatchTask(task: TaskItem): boolean {
    return !!task.taskType && MATCH_TASK_TYPES.has(task.taskType);
}

/**
 * Typed accessor for a payment_request task's purpose. `taskData` is an untyped
 * bag (Record<string, unknown>), so this narrows the magic literal in one spot;
 * returns null when the value is absent or not a known purpose.
 */
export function getPaymentRequestPurpose(task: TaskItem): PaymentRequestPurpose | null {
    const purpose = task.taskData?.purpose;
    if (purpose === 'emd' || purpose === 'cashToClose' || purpose === 'other') return purpose;
    return null;
}

/**
 * Single source of truth mapping a task to the logical "effective type" that
 * priming, completion, and TaskCard all key their lookup maps off of. Keeping
 * this in one module prevents those three consumers from drifting on how aliases
 * resolve (the drift the old "Mirrors resolveEffectiveType" comments warned about).
 *
 * - complete_scc_request wraps send/collect/confirm via taskData.request_type.
 * - A payment_request resolves by taskData.digital_payment_accepted:
 *   false → `send` (the wire-instructions experience — no digital payment offered);
 *   true → purpose-specific split (DS-3309): EMD → `payment_request_emd`,
 *   cash-to-close → `payment_request_c2c`, each with its own copy/priming.
 *   When the flag is absent (or the purpose is unknown under a true flag) the raw
 *   `payment_request` type is kept as a fallback.
 * - Every other task keeps its raw type, so each consumer applies its own fallback.
 */
export function resolveEffectiveType(task: TaskItem): string | null {
    const type = task.taskType;
    if (!type) return null;
    if (type === 'complete_scc_request') {
        return (task.taskData?.request_type as string) ?? 'send';
    }
    if (type === 'payment_request') {
        const digitalAccepted = task.taskData?.digital_payment_accepted;
        // No digital payment offered → route to the wire-instructions (send) experience.
        if (digitalAccepted === false) return 'send';
        const purpose = getPaymentRequestPurpose(task);
        // An Other payment never takes the closing-funds fallback, even without the digital flag.
        if (purpose === 'other') return PAYMENT_REQUEST_OTHER;
        // Digital payment offered → purpose-specific copy/priming.
        if (digitalAccepted === true) {
            if (purpose === 'emd') return PAYMENT_REQUEST_EMD;
            if (purpose === 'cashToClose') return PAYMENT_REQUEST_C2C;
        }
    }
    return type;
}

/**
 * D24 — every effective type the platform can put in front of a participant. The coverage spec
 * walks this list, so a type added without its label, priming and completion entries fails a
 * test rather than degrading silently. payment_emd and payment_c2c went unregistered for five
 * months exactly that way.
 *
 * Add a type here when the platform starts emitting it, not when a map happens to gain a key.
 */
export const EMITTED_EFFECTIVE_TYPES = [
    // PaymentTransfer-backed. payment_other has a completion view but no priming (see EMITTED_PAYMENT_EFFECTIVE_TYPES).
    'payment_emd',
    'payment_c2c',
    'payment_repayment',
    'payment_other',
    // Request-backed, split by purpose. The raw type survives only when the flag is absent.
    'payment_request',
    PAYMENT_REQUEST_EMD,
    PAYMENT_REQUEST_C2C,
    PAYMENT_REQUEST_OTHER,
    // SCC sub-flows.
    'send',
    'collect',
    'confirm',
    'match',
    'complete_match_request',
    'document_signing',
    'fraud_education',
] as const;

/**
 * The subset that must reach a completion view and a priming config. Excludes the
 * payment_request_* pair by design: those are Request-backed and re-enter the live flow instead
 * of opening a completion sheet. See taskViews.ts.
 */
export const EMITTED_PAYMENT_EFFECTIVE_TYPES = [
    'payment_emd',
    'payment_c2c',
    'payment_repayment',
] as const;
